import React, { useState, useEffect } from 'react';
import { Card, Row } from 'react-bootstrap';
import ListaEquipamentos from './components/ListaEquipamentos/ListaEquipamentos';
import Map from './components/Map/Map';
import SensorDetails from './components/SensorDetails/SensorDetails';
import Reload from '../../components/reload/reload';
import Page from '../../components/Page/Page';
import Headers from '../../components/Headers/Headers';
import './SilosBag.css';
import AlertModal from '../../components/AlertModal/AlertModal';
import WelcomeModal from '../../components/WelcomeModal/WelcomeModal';
import ApiMServer from '../../services/ApiMServer';

// Função para converter voltagem em porcentagem usando interpolação linear
const voltageToPercent = (voltage) => {
  // Tabela de pontos conhecidos da curva de descarga (ordenada por tensão decrescente)
  const points = [
    { v: 4.20, p: 100 },
    { v: 4.05, p: 95 },
    { v: 3.90, p: 80 },
    { v: 3.80, p: 65 },
    { v: 3.70, p: 50 },
    { v: 3.60, p: 40 },
    { v: 3.50, p: 25 },
    { v: 3.40, p: 19 },
    { v: 3.30, p: 10 },
    { v: 3.00, p: 1 },
  ];
  
  if (voltage >= 4.10) return 100;
  if (voltage < 3.00) return 0;
  
  for (let i = 0; i < points.length - 1; i++) {
    if (voltage >= points[i + 1].v && voltage <= points[i].v) {
      const v1 = points[i + 1].v;
      const v2 = points[i].v;
      const p1 = points[i + 1].p;
      const p2 = points[i].p;
      
      const percent = p1 + ((voltage - v1) / (v2 - v1)) * (p2 - p1);
      return Math.round(percent);
    }
  }
  
  return 0;
};

// Função para calcular porcentagem de sinal baseado no RSSI
const rssiToPercent = (rssi) => {
  if (rssi === 0 || rssi >= -40) return 100; // Excelente
  if (rssi >= -60) return 75; // Bom
  if (rssi >= -80) return 50; // Normal
  if (rssi >= -100) return 25; // Regular
  return 0; // Fraco
};

// Função para transformar slave em formato esperado pelo SensorDetails
const transformSlaveForDetails = (slave) => {
  return {
    serial: slave.serialnumber,
    name: `Silo Bag ${slave.serialnumber}`,
    lat: slave.location.coordinates[1],
    lng: slave.location.coordinates[0],
    city: "Fazenda",
    state: "MT",
    status: 'online',
    alerts: [],
    battery: 0, // Será atualizado quando buscar dados
    signal: 0, // Será atualizado quando buscar dados
    voltage: 0, // Será atualizado quando buscar dados
    rssi: 0, // Será atualizado quando buscar dados
    measurements: {
      lastUpdate: new Date()
    }
  };
};

const SilosBag = () => {
  // Estados principais
  const [masters, setMasters] = useState([]);
  const [selectedMaster, setSelectedMaster] = useState(null);
  const [selectedSensorForDetails, setSelectedSensorForDetails] = useState(null);
  const [showDetails, setShowDetails] = useState(false);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [msg, setMsg] = useState({ title: "", message: "" });

  const [cooperative] = useState(JSON.parse(localStorage.getItem('cooperative')));

  const [user,] = useState(() => {
    const storedUser = localStorage.getItem('user');
    return storedUser ? JSON.parse(storedUser) : null;
  });

  const handleShow = () => setShowModal(true);
  const handleClose = () => setShowModal(false);

  // useEffect PRINCIPAL - Busca masters
  useEffect(() => {
    const companyId = cooperative?._id;
    if (!companyId) {
        console.warn("ID da empresa não encontrado no localStorage. Polling não iniciado.");
        setLoading(false);
        return;
    }

    let isMounted = true;
    const fetchMasters = async () => {
        try {
            const response = await ApiMServer.get(`/v2/masters?companyId=${companyId}`);
            if (!isMounted) return;

            const mastersData = response.data;
            if (mastersData && mastersData.length > 0) {
                setMasters(mastersData);
                
                // Seleciona primeiro master automaticamente se ainda não houver seleção
                if (!selectedMaster) {
                    setSelectedMaster(mastersData[0]);
                }
            }
        } catch (error) {
            console.error("Erro ao buscar masters:", error);
        } finally {
            if (loading && isMounted) setLoading(false);
        }
    };

    fetchMasters(); // Primeira chamada imediata
    const intervalId = setInterval(fetchMasters, 30000); // Polling a cada 30 segundos
    
    return () => {
        isMounted = false;
        clearInterval(intervalId);
    };
  }, [cooperative, selectedMaster, loading]);


  // Handlers
  const handleMasterSelect = (master) => {
    setSelectedMaster(master);
    setShowDetails(false); // Fecha detalhes ao trocar master
    setSelectedSensorForDetails(null);
  };

  const handleSlaveClick = (slaveSerial) => {
    if (!selectedMaster) return;
    
    // Encontra o slave no master selecionado
    const slave = selectedMaster.slaves.find(s => s.serialnumber === slaveSerial);
    if (slave) {
      const sensorData = transformSlaveForDetails(slave);
      setSelectedSensorForDetails(sensorData);
      setShowDetails(true);
    }
  };

  const handleCloseSensorDetails = () => {
    setShowDetails(false);
    setSelectedSensorForDetails(null);
  };

  return (
    <Page title={"Silos Bag"}>
      {loading && <Reload />}

      <Headers page="Silos Bag" />

      <WelcomeModal />

      <Row className="silos-bag-container g-2">

          {(user?.permissionSGU?.home?.read || !user) ?
            <div className="silos-bag-content-row">
              <AlertModal
                show={showModal}
                handleClose={handleClose}
                msg={msg}
              />

              {/* Lista lateral - Masters */}
              <div className="silos-bag-sidebar">
                <ListaEquipamentos
                  masters={masters}
                  selectedMaster={selectedMaster}
                  onMasterSelect={handleMasterSelect}
                />
              </div>

              {/* Mapa ou Detalhes do Sensor */}
              <div className="silos-bag-map-container">
                {showDetails && selectedSensorForDetails ? (
                  <SensorDetails
                    sensor={selectedSensorForDetails}
                    onClose={handleCloseSensorDetails}
                  />
                ) : (
                  <Map
                    selectedMaster={selectedMaster}
                    onSlaveClick={handleSlaveClick}
                  />
                )}
              </div>
            </div>
            : (
              <Card className='p-2'>
                <h2 className='text-center'>
                  Você não tem permissão para acessar o sistema. <br />
                  Entre em contato com o administrador!
                </h2>
              </Card>
            )
          }
      </Row>
    </Page>
  );
};

export default SilosBag;